import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useAuth } from '../../contexts/AuthContext';
import { aiAPI } from '../../services/api';
import SafeMarkdown from '../../components/ai/SafeMarkdown';
import VerifyToUnlock from '../../components/ai/VerifyToUnlock';

interface Msg { id?: number | string; role: 'user' | 'assistant'; content: string; tools?: string[]; pending?: boolean }
interface Conversation { id: number; title: string; updated_at: string; message_count: number }

const SUGGESTIONS = [
  'Why did my traffic change this week?',
  'Is my site secure? What should I fix first?',
  'How do I enable HSTS on nginx?',
  'Explain my most recent alert',
  'How is my SEO doing?',
  'Was my site down recently?',
];

const TOOL_LABEL: Record<string, string> = {
  get_analytics_summary: 'analytics', get_uptime_and_incidents: 'uptime', get_security_findings: 'security findings',
  get_recent_alerts: 'alerts', get_seo_summary: 'SEO', get_site_overview: 'site overview',
};

const AiAssistantPage: React.FC = () => {
  useDocumentTitle('AI Assistant');
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;
  const [status, setStatus] = useState<{ available: boolean; verified: boolean; usage?: any } | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [activity, setActivity] = useState<string>('');
  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const loadConversations = useCallback(async () => {
    if (!assetId) return;
    try { const r = await aiAPI.conversations(assetId); setConversations(r.data.data || []); } catch { /* ignore */ }
  }, [assetId]);

  useEffect(() => {
    if (!assetId) return;
    setStatus(null); setMessages([]); setConversationId(null);
    aiAPI.status(assetId)
      .then((r) => setStatus(r.data.data))
      .catch(() => setStatus({ available: false, verified: !!currentAsset?.is_verified }));
    loadConversations();
    return () => abortRef.current?.abort();
  }, [assetId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages, activity]);

  const openConversation = async (id: number) => {
    if (!assetId || sending) return;
    try {
      const r = await aiAPI.conversation(assetId, id);
      setConversationId(id);
      setMessages((r.data.data.messages || []).map((m: any) => ({
        id: m.id, role: m.role, content: m.content,
        tools: Array.isArray(m.tool_calls) ? m.tool_calls.map((t: any) => t.name) : undefined,
      })));
    } catch { toast.error('Could not open conversation'); }
  };

  const removeConversation = async (id: number) => {
    if (!assetId) return;
    try {
      await aiAPI.deleteConversation(assetId, id);
      if (conversationId === id) { setConversationId(null); setMessages([]); }
      loadConversations();
    } catch { toast.error('Could not delete conversation'); }
  };

  const send = async (text?: string) => {
    const message = (text ?? input).trim();
    if (!assetId || !message || sending) return;
    setInput('');
    setSending(true);
    setActivity('Thinking…');
    const tools: string[] = [];
    setMessages((m) => [...m, { role: 'user', content: message }, { role: 'assistant', content: '', pending: true }]);
    const updateLast = (fn: (m: Msg) => Msg) =>
      setMessages((all) => { const copy = [...all]; copy[copy.length - 1] = fn(copy[copy.length - 1]); return copy; });

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await aiAPI.chatStream(assetId, message, conversationId, (type, data) => {
        if (type === 'meta' && data.conversationId) setConversationId(Number(data.conversationId));
        else if (type === 'tool') {
          tools.push(data.name);
          setActivity(`Checking your ${TOOL_LABEL[data.name] || 'data'}…`);
        } else if (type === 'delta') {
          setActivity('');
          updateLast((m) => ({ ...m, content: m.content + data.text }));
        } else if (type === 'done') {
          updateLast((m) => ({ ...m, pending: false, tools: data.toolCalls || tools, id: data.messageId }));
        } else if (type === 'error') {
          updateLast((m) => ({ ...m, pending: false, content: m.content || `⚠️ ${data.message || 'Something went wrong'}` }));
          if (data.code === 'ASSET_NOT_VERIFIED') setStatus((s) => (s ? { ...s, verified: false } : s));
        }
      }, controller.signal);
    } catch (e: any) {
      if (e?.name !== 'AbortError') updateLast((m) => ({ ...m, pending: false, content: m.content || '⚠️ Connection lost. Please try again.' }));
    } finally {
      updateLast((m) => ({ ...m, pending: false }));
      setSending(false);
      setActivity('');
      loadConversations();
    }
  };

  const newChat = () => { if (!sending) { setConversationId(null); setMessages([]); } };

  return (
    <DashboardLayout>
      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-0"><i className="bi bi-chat-dots me-2 text-primary" />AI Assistant</h4>
            <div className="text-muted small">Ask anything about {currentAsset?.name || 'your site'} — traffic, uptime, security, SEO or how to fix something.</div>
          </div>
          {status?.verified && status.available && (
            <button className="btn btn-outline-secondary btn-sm" onClick={newChat} disabled={sending}><i className="bi bi-plus-lg me-1" />New chat</button>
          )}
        </div>

        {!status ? (
          <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
        ) : !status.verified ? (
          <VerifyToUnlock feature="the AI assistant" />
        ) : !status.available ? (
          <div className="alert alert-warning">The AI assistant is temporarily unavailable. Please try again later.</div>
        ) : (
          <div className="row g-3">
            <div className="col-lg-3 d-none d-lg-block">
              <div className="card border-0 shadow-sm h-100"><div className="card-body p-2">
                <div className="small text-muted fw-semibold px-2 mb-2">Conversations</div>
                {conversations.length === 0 && <div className="small text-muted px-2">No conversations yet.</div>}
                <div className="list-group list-group-flush">
                  {conversations.map((c) => (
                    <div key={c.id} className={`list-group-item list-group-item-action d-flex align-items-center px-2 py-2 small ${conversationId === c.id ? 'active' : ''}`}
                      role="button" onClick={() => openConversation(c.id)}>
                      <span className="text-truncate flex-grow-1" title={c.title}>{c.title}</span>
                      <button type="button" className={`btn btn-sm btn-link p-0 ms-2 ${conversationId === c.id ? 'text-white' : 'text-muted'}`}
                        title="Delete" onClick={(e) => { e.stopPropagation(); removeConversation(c.id); }}>
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  ))}
                </div>
              </div></div>
            </div>

            <div className="col-lg-9">
              <div className="card border-0 shadow-sm d-flex flex-column" style={{ height: 'calc(100vh - 220px)', minHeight: 420 }}>
                <div className="card-body overflow-auto flex-grow-1">
                  {messages.length === 0 && (
                    <div className="text-center py-4">
                      <i className="bi bi-stars text-primary" style={{ fontSize: '2.2rem' }} />
                      <p className="text-muted mt-2">I can look up your site's analytics, uptime, security findings, alerts and SEO data.</p>
                      <div className="d-flex flex-wrap justify-content-center gap-2 mx-auto" style={{ maxWidth: 680 }}>
                        {SUGGESTIONS.map((s) => (
                          <button key={s} className="btn btn-sm btn-outline-primary rounded-pill" onClick={() => send(s)}>{s}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <div key={i} className={`d-flex mb-3 ${m.role === 'user' ? 'justify-content-end' : ''}`}>
                      <div className={`rounded-3 px-3 py-2 ${m.role === 'user' ? 'bg-primary text-white' : 'bg-light'}`} style={{ maxWidth: '85%' }}>
                        {m.role === 'user' ? (
                          <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
                        ) : (
                          <>
                            {m.content ? <SafeMarkdown text={m.content} /> : (
                              <span className="text-muted small"><span className="spinner-grow spinner-grow-sm me-2" />{activity || 'Thinking…'}</span>
                            )}
                            {m.content && m.pending && activity && <div className="text-muted small mt-1">{activity}</div>}
                            {!m.pending && m.tools && m.tools.length > 0 && (
                              <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                                <i className="bi bi-database me-1" />Used your {Array.from(new Set(m.tools.map((t) => TOOL_LABEL[t] || t))).join(', ')} data
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
                <div className="card-footer bg-white border-0">
                  <form onSubmit={(e) => { e.preventDefault(); send(); }} className="d-flex gap-2">
                    <textarea className="form-control" rows={1} value={input} maxLength={4000} placeholder="Ask about your site…"
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                      disabled={sending} style={{ resize: 'none' }} />
                    <button type="submit" className="btn btn-primary" disabled={sending || !input.trim()}>
                      {sending ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-send" />}
                    </button>
                  </form>
                  <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
                    AI answers can be wrong — double-check important changes. The assistant only sees data for this site.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AiAssistantPage;
